/**
 * useBulkEditTasks - Hook para edição em massa de tarefas
 * Chama RPC bulk_edit_tasks com validação atômica server-side
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-04-09 v1.0.0 - Criação do hook
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('BulkEditTasks');

/**
 * Dados para edição em massa de tarefas
 */
export interface BulkEditTasksData {
  /** IDs das tarefas a editar */
  taskIds: string[];
  /** Categoria destino */
  categoryId?: string;
  /** Fase destino (deve pertencer à categoria) */
  phaseId?: string;
  /** Classificação destino (deve pertencer à categoria) */
  classificationId?: string;
  /** Nova prioridade */
  priority?: string;
  /** Novo responsável */
  assignedTo?: string;
  /** Novo prazo (ISO string ou null para limpar) */
  dueDate?: string | null;
}

/**
 * Hook para edição em massa de tarefas via RPC
 * Invalida queries de tarefas após sucesso
 */
export function useBulkEditTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkEditTasksData) => {
      logger.info('Executando edição em massa de tarefas', {
        taskCount: data.taskIds.length,
        hasFields: !!(data.categoryId || data.phaseId || data.classificationId || data.priority || data.assignedTo),
        hasDueDate: data.dueDate !== undefined,
      });

      // Monta JSONB de atualizações (apenas campos preenchidos)
      const updates: Record<string, unknown> = {};
      if (data.categoryId) updates.category_id = data.categoryId;
      if (data.phaseId) updates.phase_id = data.phaseId;
      if (data.classificationId) updates.classification_id = data.classificationId;
      if (data.priority) updates.priority = data.priority;
      if (data.assignedTo) updates.assigned_to = data.assignedTo;
      if (data.dueDate !== undefined) updates.due_date = data.dueDate;

      // Verifica se há algo para atualizar
      if (Object.keys(updates).length === 0) {
        logger.warn('Nenhum campo para atualizar');
        return 0;
      }

      const { data: result, error } = await supabase.rpc('bulk_edit_tasks', {
        p_task_ids: data.taskIds,
        p_updates: updates as unknown as Record<string, never>,
      });

      if (error) {
        logger.error('Erro na edição em massa de tarefas', error);
        throw error;
      }

      return result as number;
    },
    onSuccess: (count, variables) => {
      // Invalida queries globais de tarefas
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['task-list-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['task-edit-data'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban'] });

      // Invalida cache individual de cada tarefa editada (modal, detalhes)
      for (const taskId of variables.taskIds) {
        queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
      }

      toast.success(`${count} tarefa(s) atualizada(s) com sucesso`);
    },
    onError: (error: Error) => {
      logger.error('Falha na edição em massa de tarefas', error);
      toast.error(error.message || 'Erro ao editar tarefas em massa');
    },
  });
}
